import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApiClient } from "../../lib/adminApiClient";
import { queryKeys } from "../../lib/queryClient";
import { ErrorBanner, LoadingState, describeError } from "../../components/States";
import { formatDateTime } from "../../lib/format";

export function AdminMaintenancePage() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKeys.adminMaintenanceMode,
    queryFn: () => adminApiClient.getMaintenanceMode(),
  });
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (data) setMessage(data.message ?? "");
  }, [data]);

  const setMode = useMutation({
    mutationFn: (enabled: boolean) => adminApiClient.setMaintenanceMode({ enabled, message }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.adminMaintenanceMode }),
  });

  if (isLoading) return <LoadingState label="Loading maintenance mode…" />;
  if (isError) return <ErrorBanner error={error} onRetry={() => void refetch()} />;
  if (!data) return null;

  return (
    <div className="stack" style={{ maxWidth: 560 }}>
      <div>
        <h1>Maintenance mode</h1>
        <p className="muted text-sm">
          When on, every buyer/seller-facing page and API route is blocked with a takeover page —
          nobody can browse, buy, message, or manage listings until it's turned back off. This
          dashboard stays reachable regardless.
        </p>
      </div>

      <div className="card stack">
        <div className="row row--between">
          <span className="muted">Current status</span>
          <span className={data.enabled ? "badge badge--danger" : "badge badge--success"}>
            {data.enabled ? "ON — site is blocked" : "OFF — site is live"}
          </span>
        </div>
        <div className="row row--between">
          <span className="muted">Last changed</span>
          <span>{formatDateTime(data.updatedAt)}</span>
        </div>
      </div>

      <div className="card stack">
        <div className="field">
          <label htmlFor="maintenance-message">Message shown to visitors (optional)</label>
          <textarea
            id="maintenance-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="MarketOnBase is temporarily down for maintenance. Please check back shortly."
          />
        </div>

        {setMode.isError && (
          <p className="field-error-text" role="alert">
            {describeError(setMode.error)}
          </p>
        )}

        <div className="row">
          {data.enabled ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={setMode.isPending}
              onClick={() => setMode.mutate(false)}
            >
              Turn maintenance mode off
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-danger"
              disabled={setMode.isPending}
              onClick={() => {
                if (window.confirm("This blocks the entire site for every visitor. Continue?")) {
                  setMode.mutate(true);
                }
              }}
            >
              Turn maintenance mode on
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
